import React, { useRef } from 'react';
import { X, Printer } from 'lucide-react';
import { formatCurrency, formatDate } from '../utils/helpers';

const InvoiceModal = ({ order, onClose }) => {
  const printRef = useRef();

  if (!order) return null;

  const subtotal = order.lineItems?.reduce((s, i) => s + i.qty * i.price, 0) || 0;
  const deliveryCharge = order.deliveryCharge || 0;
  const grandTotal = order.total || subtotal + deliveryCharge;

  const handlePrint = () => {
    const content = printRef.current.innerHTML;
    const win = window.open('', '_blank', 'width=800,height=900');
    win.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Invoice - ${order.id}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: 'Segoe UI', Arial, sans-serif; color: #111; background: #fff; padding: 40px; }
            .invoice-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 32px; padding-bottom: 20px; border-bottom: 2px solid #6366f1; }
            .brand { font-size: 24px; font-weight: 800; color: #6366f1; letter-spacing: -0.5px; }
            .brand span { color: #111; }
            .invoice-meta { text-align: right; }
            .invoice-meta h2 { font-size: 28px; font-weight: 700; color: #6366f1; letter-spacing: 2px; }
            .invoice-meta p { font-size: 13px; color: #555; margin-top: 4px; }
            .section { margin-bottom: 24px; }
            .section-title { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; color: #6366f1; margin-bottom: 8px; }
            .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
            .info-box { background: #f8f8ff; border-radius: 8px; padding: 14px; }
            .info-box p { font-size: 12px; color: #777; margin-bottom: 2px; }
            .info-box strong { font-size: 14px; color: #111; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 16px; }
            thead tr { background: #6366f1; color: #fff; }
            thead th { padding: 10px 12px; text-align: left; font-size: 12px; font-weight: 600; }
            tbody tr { border-bottom: 1px solid #eee; }
            tbody tr:last-child { border-bottom: none; }
            tbody td { padding: 10px 12px; font-size: 13px; color: #333; }
            .totals { margin-left: auto; width: 260px; }
            .totals-row { display: flex; justify-content: space-between; padding: 6px 0; font-size: 13px; color: #555; border-bottom: 1px solid #eee; }
            .totals-row.grand { font-size: 16px; font-weight: 700; color: #111; border-bottom: none; padding-top: 10px; }
            .status-badge { display: inline-block; padding: 3px 10px; border-radius: 20px; font-size: 11px; font-weight: 600; background: #e0e7ff; color: #4338ca; text-transform: capitalize; }
            .footer { margin-top: 40px; padding-top: 16px; border-top: 1px solid #eee; text-align: center; font-size: 12px; color: #999; }
          </style>
        </head>
        <body>${content}</body>
      </html>
    `);
    win.document.close();
    win.focus();
    setTimeout(() => { win.print(); win.close(); }, 300);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-h-[90vh] flex flex-col">
        {/* Modal Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Invoice — {order.id}</h2>
          <div className="flex items-center gap-2">
            <button
              onClick={handlePrint}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors"
            >
              <Printer className="w-4 h-4" /> Print / Save PDF
            </button>
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Invoice Content */}
        <div className="overflow-y-auto p-6 flex-1">
          <div ref={printRef}>
            {/* Header */}
            <div className="invoice-header flex justify-between items-start mb-8 pb-5 border-b-2 border-indigo-500">
              <div>
                <p className="text-2xl font-extrabold text-indigo-600 tracking-tight">
                  Medi<span className="text-gray-900 dark:text-white">Hub</span>
                </p>
                <p className="text-xs text-gray-500 mt-1">Your trusted medicine delivery platform</p>
              </div>
              <div className="text-right">
                <p className="text-3xl font-bold text-indigo-600 tracking-widest">INVOICE</p>
                <p className="text-sm font-mono font-semibold text-gray-700 dark:text-gray-300 mt-1">{order.id}</p>
                <p className="text-xs text-gray-500 mt-0.5">Date: {formatDate(order.date)}</p>
              </div>
            </div>

            {/* Bill To + Order Info */}
            <div className="info-grid grid grid-cols-2 gap-4 mb-6">
              <div className="bg-indigo-50 dark:bg-indigo-900/20 rounded-xl p-4">
                <p className="text-xs font-bold uppercase tracking-widest text-indigo-500 mb-2">Bill To</p>
                <p className="font-semibold text-gray-900 dark:text-white">{order.patient}</p>
                <p className="text-sm text-gray-500 mt-1">{order.address || '—'}</p>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700/40 rounded-xl p-4">
                <p className="text-xs font-bold uppercase tracking-widest text-indigo-500 mb-2">Order Info</p>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Order Date</span>
                    <span className="font-medium text-gray-900 dark:text-white">{formatDate(order.date)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Delivery Date</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {order.deliveryDate ? formatDate(order.deliveryDate) : 'Pending'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-500">Status</span>
                    <span className="capitalize text-xs font-semibold px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300">
                      {order.status?.replace(/-/g, ' ')}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Delivery Info */}
            {order.deliveryBoyName && (
              <div className="bg-orange-50 dark:bg-orange-900/20 rounded-xl p-4 mb-6 grid grid-cols-3 gap-3 text-sm">
                <div>
                  <p className="text-xs text-gray-500 mb-0.5">Delivery By</p>
                  <p className="font-semibold text-gray-900 dark:text-white">{order.deliveryBoyName}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-0.5">Delivery Charge</p>
                  <p className="font-semibold text-gray-900 dark:text-white">{formatCurrency(deliveryCharge)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-0.5">ETA</p>
                  <p className="font-semibold text-gray-900 dark:text-white">{order.estimatedDeliveryTime || '—'}</p>
                </div>
              </div>
            )}

            {/* Line Items Table */}
            <div className="mb-4">
              <p className="text-xs font-bold uppercase tracking-widest text-indigo-500 mb-2">Order Items</p>
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-indigo-600 text-white">
                    {['#', 'Medicine', 'Qty', 'Unit Price', 'Total'].map(h => (
                      <th key={h} className="px-3 py-2.5 text-left text-xs font-semibold">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {order.lineItems?.map((item, i) => (
                    <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                      <td className="px-3 py-2.5 text-gray-400 text-xs">{i + 1}</td>
                      <td className="px-3 py-2.5 font-medium text-gray-900 dark:text-white">{item.name}</td>
                      <td className="px-3 py-2.5 text-gray-600 dark:text-gray-400">{item.qty}</td>
                      <td className="px-3 py-2.5 text-gray-600 dark:text-gray-400">{formatCurrency(item.price)}</td>
                      <td className="px-3 py-2.5 font-semibold text-gray-900 dark:text-white">{formatCurrency(item.qty * item.price)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Totals */}
            <div className="flex justify-end">
              <div className="w-64 space-y-1.5">
                <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 pb-1.5 border-b border-gray-200 dark:border-gray-700">
                  <span>Subtotal</span>
                  <span>{formatCurrency(subtotal)}</span>
                </div>
                <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 pb-1.5 border-b border-gray-200 dark:border-gray-700">
                  <span>Delivery Charge</span>
                  <span>{formatCurrency(deliveryCharge)}</span>
                </div>
                <div className="flex justify-between text-base font-bold text-gray-900 dark:text-white pt-1">
                  <span>Grand Total</span>
                  <span className="text-indigo-600 dark:text-indigo-400">{formatCurrency(grandTotal)}</span>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="footer mt-10 pt-4 border-t border-gray-200 dark:border-gray-700 text-center text-xs text-gray-400">
              <p>Thank you for choosing MediHub1! For support, contact support@medihub1.com</p>
              <p className="mt-1">This is a computer-generated invoice and does not require a signature.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceModal;
